class InlineZoomWrapper extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.mediaEl = this.querySelector('.product-media');
    if (!this.mediaEl) return;

    this.translateX = 0;
    this.translateY = 0;
    this.lastTouchX = 0;
    this.lastTouchY = 0;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.movedDistance = 0;
    this.zoomAssetLoaded = false;

    // Ensure the underlying media image itself lazy-loads if it's an <img>
    if (this.mediaEl.tagName === 'IMG' && !this.mediaEl.hasAttribute('loading')) {
      this.mediaEl.setAttribute('loading', 'lazy');
    }

    // Defer binding interaction listeners until the element is near the viewport
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.bindInteractionListeners();
              this.observer.disconnect();
            }
          });
        },
        { rootMargin: '200px' }
      );
      this.observer.observe(this);
    } else {
      // Fallback for browsers without IntersectionObserver support
      this.bindInteractionListeners();
    }
  }

  bindInteractionListeners() {
    if (this.listenersBound) return;
    this.listenersBound = true;

    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('mousemove', this.handleMouseMove.bind(this));

    this.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
    this.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.addEventListener('touchend', this.handleTouchEnd.bind(this));
  }

  getScale() {
    const value = getComputedStyle(this).getPropertyValue('--inline-zoom-scale');
    return parseFloat(value) || 2;
  }

  loadZoomAssetIfNeeded() {
    if (this.zoomAssetLoaded) return;

    const zoomSrc = this.mediaEl.dataset.zoomSrc;
    if (zoomSrc && this.mediaEl.tagName === 'IMG') {
      this.mediaEl.src = zoomSrc;
    }

    this.zoomAssetLoaded = true;
  }

  applyTransform() {
    this.mediaEl.style.transformOrigin = 'center center';
    this.mediaEl.style.transform = `scale(${this.getScale()}) translate(${this.translateX}px, ${this.translateY}px)`;
  }

  resetTransform() {
    this.translateX = 0;
    this.translateY = 0;
    this.mediaEl.style.transform = '';
    this.mediaEl.style.transformOrigin = 'center center';
  }

  clampTranslate() {
    const rect = this.getBoundingClientRect();
    const scale = this.getScale();
    const maxX = (rect.width * (scale - 1)) / (2 * scale);
    const maxY = (rect.height * (scale - 1)) / (2 * scale);

    this.translateX = Math.max(-maxX, Math.min(maxX, this.translateX));
    this.translateY = Math.max(-maxY, Math.min(maxY, this.translateY));
  }

  handleClick(event) {
    if (event.pointerType === 'touch') return;

    if (this.classList.contains('is-zoomed')) {
      this.classList.remove('is-zoomed');
      this.resetTransform();
      return;
    }

    this.loadZoomAssetIfNeeded();

    const rect = this.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;

    this.translateX = (0.5 - x) * rect.width * 0.4;
    this.translateY = (0.5 - y) * rect.height * 0.4;
    this.clampTranslate();
    this.classList.add('is-zoomed');
    this.applyTransform();
  }

  handleMouseMove(event) {
    if (!this.classList.contains('is-zoomed') || event.pointerType === 'touch') return;

    const rect = this.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;

    this.translateX = (0.5 - x) * rect.width * 0.4;
    this.translateY = (0.5 - y) * rect.height * 0.4;
    this.clampTranslate();
    this.applyTransform();
  }

  handleTouchStart(event) {
    if (event.touches.length !== 1) return;

    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.lastTouchX = this.touchStartX;
    this.lastTouchY = this.touchStartY;
    this.movedDistance = 0;
    this.wasZoomedBeforeTouch = this.classList.contains('is-zoomed');
  }

  handleTouchMove(event) {
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];

    if (!this.classList.contains('is-zoomed')) {
      this.movedDistance += Math.abs(touch.clientX - this.lastTouchX) + Math.abs(touch.clientY - this.lastTouchY);
      this.lastTouchX = touch.clientX;
      this.lastTouchY = touch.clientY;
      return;
    }

    event.preventDefault();

    const scale = this.getScale();
    const deltaX = (touch.clientX - this.lastTouchX) / scale;
    const deltaY = (touch.clientY - this.lastTouchY) / scale;

    this.movedDistance += Math.abs(touch.clientX - this.lastTouchX) + Math.abs(touch.clientY - this.lastTouchY);

    this.translateX += deltaX;
    this.translateY += deltaY;
    this.clampTranslate();
    this.applyTransform();

    this.lastTouchX = touch.clientX;
    this.lastTouchY = touch.clientY;
  }

  handleTouchEnd() {
    const wasTap = this.movedDistance < 10;

    if (!wasTap) return;

    if (this.wasZoomedBeforeTouch) {
      this.classList.remove('is-zoomed');
      this.resetTransform();
    } else {
      this.loadZoomAssetIfNeeded();
      this.classList.add('is-zoomed');
      this.translateX = 0;
      this.translateY = 0;
      this.applyTransform();
    }
  }
}

customElements.define('inline-zoom-wrapper', InlineZoomWrapper);